import { useEffect } from 'react';
import { useViewerStore, getViewportMode } from '../stores/viewerStore';
import { csToolsUtilities } from '../cornerstone/init';
import styles from './FloatingControls.module.css';

interface Props {
  /** The active viewport element for cine playback */
  getActiveViewportElement: () => HTMLDivElement | null;
}

export default function FloatingControls({ getActiveViewportElement }: Props) {
  const {
    viewportModes, viewportSlabs, cineActive, cineFps,
    activeViewportIndex, setViewportSlab, setCineActive, setCineFps,
  } = useViewerStore();

  const activeTool = useViewerStore((s) => s.activeTool);
  // Cine only exposed on stack viewports (single-frame series can't play).
  const activeMode = getViewportMode(viewportModes, activeViewportIndex);
  const showCine = activeMode === 'stack' && (activeTool === 'Cine' || cineActive);
  // Slab controls target the ACTIVE viewport. Falls back to 50mm.
  const activeSlab = viewportSlabs[activeViewportIndex] ?? 50;

  // When the user toggles off the Cine tool (clicks Cine button while it's
  // active), the toolbar reverts activeTool to 'Wwwc'. Stop any running
  // playback loop and clear the controls so a stale "playing" indicator
  // doesn't linger. (#8)
  useEffect(() => {
    if (activeTool !== 'Cine' && cineActive) {
      const el = getActiveViewportElement();
      if (el) { try { csToolsUtilities.cine.stopClip(el); } catch {} }
      setCineActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool]);
  // Show MIP slab controls if the active viewport is one of the MIP modes.
  const showMip = activeMode === 'mip-ax' || activeMode === 'mip-sag' || activeMode === 'mip-cor';
  // Show 3D slab if the active viewport is 3D-VR (used as a coarse render
  // hint for the VR compositor).
  const show3D = activeMode === '3d-vr';

  function toggleCine() {
    const el = getActiveViewportElement();
    if (!el) return;

    if (cineActive) {
      csToolsUtilities.cine.stopClip(el);
      setCineActive(false);
    } else {
      csToolsUtilities.cine.playClip(el, { framesPerSecond: cineFps, loop: true });
      setCineActive(true);
    }
  }

  function setCineSpeed(fps: number) {
    const clamped = Math.max(1, Math.min(60, fps));
    setCineFps(clamped);

    const el = getActiveViewportElement();
    if (el && cineActive) {
      csToolsUtilities.cine.stopClip(el);
      csToolsUtilities.cine.playClip(el, { framesPerSecond: clamped, loop: true });
    }
  }

  function adjustSlab(delta: number) {
    const next = Math.max(5, Math.min(200, activeSlab + delta));
    setViewportSlab(activeViewportIndex, next);
  }

  if (!showCine && !showMip && !show3D) return null;

  return (
    <div className={styles.bar}>
      {/* Cine controls */}
      {showCine && (
        <>
          <button
            className={styles.btn}
            onClick={() => setCineSpeed(cineFps - 5)}
            title="Slower"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="19,20 9,12 19,4" />
              <line x1="5" y1="4" x2="5" y2="20" />
            </svg>
          </button>

          <button
            className={`${styles.playBtn} ${cineActive ? styles.playing : ''}`}
            onClick={toggleCine}
            title={cineActive ? 'Pause' : 'Play'}
          >
            {cineActive ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="6,4 20,12 6,20" />
              </svg>
            )}
          </button>

          <button
            className={styles.btn}
            onClick={() => setCineSpeed(cineFps + 5)}
            title="Faster"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5,4 15,12 5,20" />
              <line x1="19" y1="4" x2="19" y2="20" />
            </svg>
          </button>

          <span className={styles.fpsLabel}>{cineFps} fps</span>
        </>
      )}

      {/* Separator if both visible */}
      {showCine && showMip && <div className={styles.divider} />}

      {/* MIP slab controls */}
      {showMip && (
        <>
          <span className={styles.slabTitle}>Slab</span>

          <button
            className={styles.btn}
            onClick={() => adjustSlab(-10)}
            title="Thinner"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="19,20 9,12 19,4" />
              <line x1="5" y1="4" x2="5" y2="20" />
            </svg>
          </button>

          <span className={styles.slabValue}>{activeSlab}mm</span>

          <button
            className={styles.btn}
            onClick={() => adjustSlab(10)}
            title="Thicker"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5,4 15,12 5,20" />
              <line x1="19" y1="4" x2="19" y2="20" />
            </svg>
          </button>
        </>
      )}

      {/* 3D slab thickness */}
      {show3D && (
        <>
          {(showCine || showMip) && <div className={styles.divider} />}
          <span className={styles.slabTitle}>3D Slab</span>
          <button className={styles.btn} onClick={() => adjustSlab(-10)} title="Thinner">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="19,20 9,12 19,4" /><line x1="5" y1="4" x2="5" y2="20" />
            </svg>
          </button>
          <span className={styles.slabValue}>{activeSlab}mm</span>
          <button className={styles.btn} onClick={() => adjustSlab(10)} title="Thicker">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5,4 15,12 5,20" /><line x1="19" y1="4" x2="19" y2="20" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
